import { z } from "../zod";

export function getZodFieldType(validator: z.ZodTypeAny | z.ZodSchema, field: string): z.ZodTypeAny | undefined {
  const sourceType = validator instanceof z.ZodEffects ? validator.sourceType() : validator;
  if (!("shape" in sourceType)) {
    return;
  }

  const shape: z.ZodRawShape = sourceType.shape;
  const fieldType = shape[field];

  if (!fieldType) {
    devConsole.error(`Field ${field} validation not found in shape`);
    return;
  }

  return fieldType;
}

/**
 * This is an ad-hoc solution to get the underlying Zod type of a ZodEffects type (e.g. ZodOptional, ZodNullable, etc.)
 * It works for the current cases, but it may not work for all cases.
 */
export function getUnderlyingZodType(zodType: z.ZodTypeAny | z.ZodSchema): z.ZodTypeAny {
  let underlyingType = zodType;
  while ("schema" in underlyingType._def || "innerType" in underlyingType._def) {
    underlyingType = "schema" in underlyingType._def ? underlyingType._def.schema : underlyingType._def.innerType;
  }
  return underlyingType;
}

/**
 * Extract the primitive type from a Zod Type
 * @param zodType
 * @returns
 */
export function getZodPrimitiveType(
  zodType: z.ZodTypeAny | z.ZodSchema,
): "date" | "boolean" | "number" | "string" | "object" | "array" | undefined {
  // TODO: zodType instanceOf z.ZodXXX is not reliable, it seems there are two zod libraries being used while unit testing
  switch (zodType._def.typeName) {
    case "ZodDate":
      return "date";
    case "ZodBoolean":
      return "boolean";
    case "ZodNumber":
      return "number";
    case "ZodString":
      return "string";
    case "ZodArray":
      return "array";
    case "ZodObject":
      return "object";
  }
}

export function isZodTypeRequired(validator: z.ZodTypeAny | z.ZodSchema) {
  const isOptional = "isOptional" in validator ? validator.isOptional() : false;
  const isNullable = "isNullable" in validator ? validator.isNullable() : false;

  return !isOptional && !isNullable;
}

export function analyzeZodType(zodType: z.ZodTypeAny | z.ZodSchema) {
  const underlyingZodType = getUnderlyingZodType(zodType);

  return {
    underlyingZodType,
    isRequired: isZodTypeRequired(zodType),
    type: getZodPrimitiveType(underlyingZodType),
  };
}

/**
 * Validate a setting against a whitelist of true values [1, true, "1", "true"]
 * @param value
 * @returns
 */
export function parseBoolean(value: unknown): boolean {
  if (!["number", "string", "boolean"].includes(typeof value)) return false;
  return value === true || value === 1 || value === "1" || value?.toString().toLowerCase() === "true";
}
